import { useQueryClient } from '@tanstack/react-query';
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { AddDialog } from '@/components/AddDialog';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  deleteSuperuserOrganisation,
  querySuperuserOrganisations,
  updateSuperuserOrganisation,
  wipeSuperuserOrganisationData,
} from '@/features/superuser/data/superuserOrganisations';
import { useNotifications } from '@/providers/notificationHooks';
import { queryKeys } from '@/shared/hooks';
import { useAccessToken } from '@/shared/hooks/auth';
import { fontFamilies, s } from '@/theme/tokens';
import { Box, Inline, Stack, Surface, Text } from '@/ui';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { OverflowTooltip } from '@/ui/composites/table/cells/OverflowTooltip';
import { formatDate } from '@/ui/formatters/formatDate';
import { Button, MenuItem, Select, TextField } from '@/ui/primitives';
import { PLAN_DISPLAY_NAME_BY_CODE } from '@flowqa/shared';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { GridColDef } from '@/ui/primitives';
import type { PlanCode, QueryResult, SuperuserOrganisationDto } from '@flowqa/shared';

type EditOrganisationForm = {
  id: string;
  originalName: string;
  name: string;
  planCode: PlanCode;
  trialEndsAt: string;
  userLimit: string;
  projectLimit: string;
  executionUploadLimit: string;
  deliveryEnabled: boolean;
  operationsEnabled: boolean;
};

const formatDateInput = (value: Date | string | null): string => {
  if (!value) return '';
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.valueOf())) return '';
  return parsed.toISOString().slice(0, 10);
};

export const SuperuserOrganisationsTable = (): ReactElement => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const { notify } = useNotifications();
  const [editingForm, setEditingForm] = useState<EditOrganisationForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [wiping, setWiping] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [wipeOpen, setWipeOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [wipeNameInput, setWipeNameInput] = useState('');
  const [deleteNameInput, setDeleteNameInput] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const pending = saving || wiping || deleting;

  const closeDialog = useCallback(() => {
    if (pending) return;
    setEditingForm(null);
    setWipeOpen(false);
    setDeleteOpen(false);
    setWipeNameInput('');
    setDeleteNameInput('');
    setErrorMessage(null);
  }, [pending]);

  const openDialog = useCallback((row: SuperuserOrganisationDto) => {
    setEditingForm({
      id: row.id,
      originalName: row.name,
      name: row.name,
      planCode: row.planCode,
      trialEndsAt: formatDateInput(row.trialEndsAt),
      userLimit: String(row.userLimit),
      projectLimit: String(row.projectLimit),
      executionUploadLimit:
        typeof row.executionUploadLimit === 'number' ? String(row.executionUploadLimit) : '',
      deliveryEnabled: row.deliveryEnabled,
      operationsEnabled: row.operationsEnabled,
    });
    setWipeOpen(false);
    setDeleteOpen(false);
    setWipeNameInput('');
    setDeleteNameInput('');
    setErrorMessage(null);
  }, []);

  const saveDialog = useCallback(async (): Promise<void> => {
    if (!editingForm) return;
    const trimmedName = editingForm.name.trim();
    if (trimmedName.length === 0) {
      setErrorMessage('Organisation name is required.');
      return;
    }
    const parsedUserLimit = Number.parseInt(editingForm.userLimit, 10);
    if (!Number.isFinite(parsedUserLimit) || parsedUserLimit <= 0) {
      setErrorMessage('User limit must be a whole number greater than 0.');
      return;
    }
    const parsedProjectLimit = Number.parseInt(editingForm.projectLimit, 10);
    if (!Number.isFinite(parsedProjectLimit) || parsedProjectLimit <= 0) {
      setErrorMessage('Project limit must be a whole number greater than 0.');
      return;
    }
    const parsedExecutionUploadLimit =
      editingForm.executionUploadLimit.trim().length === 0
        ? null
        : Number.parseInt(editingForm.executionUploadLimit, 10);
    if (
      parsedExecutionUploadLimit !== null &&
      (!Number.isFinite(parsedExecutionUploadLimit) || parsedExecutionUploadLimit < 0)
    ) {
      setErrorMessage('Execution upload limit must be a whole number 0 or greater.');
      return;
    }
    setSaving(true);
    setErrorMessage(null);
    try {
      const updatedOrganisation = await updateSuperuserOrganisation({
        id: editingForm.id,
        name: trimmedName,
        planCode: editingForm.planCode,
        trialEndsAt: editingForm.trialEndsAt.length > 0 ? editingForm.trialEndsAt : null,
        userLimit: parsedUserLimit,
        projectLimit: parsedProjectLimit,
        executionUploadLimit: parsedExecutionUploadLimit,
        deliveryEnabled: editingForm.deliveryEnabled,
        operationsEnabled: editingForm.operationsEnabled,
      });
      queryClient.setQueriesData<QueryResult<SuperuserOrganisationDto>>(
        { queryKey: queryKeys.superuserOrganisations() },
        (previous) => {
          if (!previous) return previous;
          return {
            ...previous,
            items: previous.items.map((item) =>
              item.id === updatedOrganisation.id ? updatedOrganisation : item,
            ),
          };
        },
      );
      await queryClient.invalidateQueries({ queryKey: queryKeys.superuserOrganisations() });
      notify({ message: 'Organisation updated', tone: 'green' });
      setEditingForm(null);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to update organisation');
    } finally {
      setSaving(false);
    }
  }, [editingForm, notify, queryClient]);

  const wipeOrganisationData = useCallback(async (): Promise<void> => {
    if (!editingForm) return;
    setWiping(true);
    setErrorMessage(null);
    try {
      await wipeSuperuserOrganisationData(editingForm.id);
      await queryClient.invalidateQueries({ queryKey: queryKeys.superuserOrganisations() });
      setWipeOpen(false);
      setWipeNameInput('');
      notify({ message: `Wiped data for ${editingForm.originalName}`, tone: 'green' });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to wipe organisation data';
      setErrorMessage(message);
      notify({ message, tone: 'red' });
    } finally {
      setWiping(false);
    }
  }, [editingForm, notify, queryClient]);

  const deleteOrganisation = useCallback(async (): Promise<void> => {
    if (!editingForm) return;
    setDeleting(true);
    setErrorMessage(null);
    try {
      await deleteSuperuserOrganisation(editingForm.id);
      await queryClient.invalidateQueries({ queryKey: queryKeys.superuserOrganisations() });
      notify({ message: `Deleted organisation ${editingForm.originalName}`, tone: 'blue' });
      setDeleteOpen(false);
      setDeleteNameInput('');
      setEditingForm(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete organisation';
      setErrorMessage(message);
      notify({ message, tone: 'red' });
    } finally {
      setDeleting(false);
    }
  }, [editingForm, notify, queryClient]);

  const columns = useMemo<Array<GridColDef<SuperuserOrganisationDto>>>(() => {
    const nameColumn: GridColDef<SuperuserOrganisationDto> = {
      field: 'name',
      headerName: 'Organisation',
      flex: 1.4,
      minWidth: 220,
      renderCell: (params) => (
        <OverflowTooltip title={params.row.name}>
          <Text role="body" component="span" truncate sx={{ display: 'block', width: '100%' }}>
            {params.row.name}
          </Text>
        </OverflowTooltip>
      ),
    };

    const createdByColumn: GridColDef<SuperuserOrganisationDto> = {
      field: 'createdByName',
      headerName: 'Created By',
      flex: 1.8,
      minWidth: 280,
      sortable: false,
      renderCell: (params) => {
        const createdByName = params.row.createdByName;
        const createdByEmail = params.row.createdByEmail;
        if (!createdByName && !createdByEmail) {
          return <Text role="body">-</Text>;
        }
        return (
          <Inline gap="xxs" align="baseline" wrap="wrap">
            {createdByName ? <Text role="body">{createdByName}</Text> : null}
            {createdByName && createdByEmail ? <Text role="body">&nbsp;</Text> : null}
            {createdByEmail ? (
              <Text role="caption" color="textSecondary">
                {createdByEmail}
              </Text>
            ) : null}
          </Inline>
        );
      },
    };

    const planTypeColumn: GridColDef<SuperuserOrganisationDto> = {
      field: 'planType',
      headerName: 'Plan Type',
      flex: 0.8,
      minWidth: 130,
      renderCell: (params) => (
        <Text role="body" component="span">
          {params.row.planType}
        </Text>
      ),
    };

    const userLimitColumn: GridColDef<SuperuserOrganisationDto> = {
      field: 'userLimit',
      headerName: 'User Limit',
      flex: 0.65,
      minWidth: 110,
      sortable: false,
      renderCell: (params) => (
        <Text role="body" component="span">
          {params.row.userUsed} / {params.row.userLimit}
        </Text>
      ),
    };

    const projectLimitColumn: GridColDef<SuperuserOrganisationDto> = {
      field: 'projectLimit',
      headerName: 'Project Limit',
      flex: 0.65,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <Text role="body" component="span">
          {params.row.projectUsed} / {params.row.projectLimit}
        </Text>
      ),
    };

    const executionUploadLimitColumn: GridColDef<SuperuserOrganisationDto> = {
      field: 'executionUploadLimit',
      headerName: 'Execution Upload Limit',
      flex: 0.85,
      minWidth: 185,
      sortable: false,
      renderCell: (params) => (
        <Text role="body" component="span">
          {params.row.executionUploadUsed} /{' '}
          {typeof params.row.executionUploadLimit === 'number'
            ? params.row.executionUploadLimit
            : 'Unlimited'}
        </Text>
      ),
    };

    const deliveryColumn: GridColDef<SuperuserOrganisationDto> = {
      field: 'deliveryEnabled',
      headerName: 'Delivery',
      flex: 0.8,
      minWidth: 120,
      type: 'boolean',
      sortable: false,
      renderCell: (params) => (
        <Text role="body" component="span">
          {params.row.deliveryEnabled ? 'Enabled' : 'Disabled'}
        </Text>
      ),
    };

    const operationsColumn: GridColDef<SuperuserOrganisationDto> = {
      field: 'operationsEnabled',
      headerName: 'Operations',
      flex: 0.8,
      minWidth: 130,
      type: 'boolean',
      sortable: false,
      renderCell: (params) => (
        <Text role="body" component="span">
          {params.row.operationsEnabled ? 'Enabled' : 'Disabled'}
        </Text>
      ),
    };

    const trialStartColumn: GridColDef<SuperuserOrganisationDto> = {
      field: 'trialStartsAt',
      headerName: 'Trial Start',
      flex: 0.85,
      minWidth: 140,
      type: 'date',
      valueGetter: (_value, row) => {
        const raw = row.trialStartsAt;
        if (!raw) return null;
        if (raw instanceof Date) return raw;
        const parsed = new Date(raw);
        return Number.isNaN(parsed.valueOf()) ? null : parsed;
      },
      renderCell: (params) => (
        <Text role="body">{formatDate(params.row.trialStartsAt) || '-'}</Text>
      ),
    };

    const trialEndColumn: GridColDef<SuperuserOrganisationDto> = {
      field: 'trialEndsAt',
      headerName: 'Trial End',
      flex: 0.85,
      minWidth: 140,
      type: 'date',
      valueGetter: (_value, row) => {
        const raw = row.trialEndsAt;
        if (!raw) return null;
        if (raw instanceof Date) return raw;
        const parsed = new Date(raw);
        return Number.isNaN(parsed.valueOf()) ? null : parsed;
      },
      renderCell: (params) => <Text role="body">{formatDate(params.row.trialEndsAt) || '-'}</Text>,
    };

    const actionColumn: GridColDef<SuperuserOrganisationDto> = {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="sm"
          fullWidthOnMobile={false}
          onClick={() => {
            openDialog(params.row);
          }}
        >
          Edit
        </Button>
      ),
    };

    return [
      nameColumn,
      createdByColumn,
      planTypeColumn,
      userLimitColumn,
      projectLimitColumn,
      executionUploadLimitColumn,
      deliveryColumn,
      operationsColumn,
      trialStartColumn,
      trialEndColumn,
      actionColumn,
    ];
  }, [openDialog]);

  const queryFn = async (input: GridQueryInput): Promise<QueryResult<SuperuserOrganisationDto>> => {
    return await querySuperuserOrganisations(input);
  };

  const expectedOrganisationName = editingForm?.originalName.trim() ?? '';
  const wipeNameMatches =
    expectedOrganisationName.length > 0 && wipeNameInput.trim() === expectedOrganisationName;
  const deleteNameMatches =
    expectedOrganisationName.length > 0 && deleteNameInput.trim() === expectedOrganisationName;
  const disableSave = !editingForm || editingForm.name.trim().length === 0 || pending;
  const codeLabelSx = {
    display: 'inline-flex',
    alignItems: 'center',
    fontFamily: fontFamilies.mono,
    backgroundColor: 'action.hover',
    borderRadius: s('xxs'),
    pl: s('sm'),
    pr: s('xs'),
  } as const;

  return (
    <>
      <Surface padding="none" border={false} sx={{ width: '100%', minWidth: 0 }}>
        <ServerDataTable<SuperuserOrganisationDto>
          queryPath="superuser-organisations"
          queryKeyPrefix={queryKeys.superuserOrganisations()}
          queryFn={queryFn}
          columns={columns}
          getRowId={(row) => row.id}
          token={accessToken}
          pageSizeOptions={[5, 10, 25, 50]}
          initialPageSize={5}
          autoHeight
          noRowsText="No organisations found"
          disableColumnMenu
        />
      </Surface>

      <AddDialog
        open={editingForm !== null}
        title="Edit Organisation"
        saving={saving}
        disableSave={disableSave}
        errorMessage={errorMessage}
        onClose={closeDialog}
        onSave={() => {
          void saveDialog();
        }}
      >
        <TextField
          label="Organisation Name"
          value={editingForm?.name ?? ''}
          onChange={(event) => {
            setEditingForm((prev) =>
              prev
                ? {
                    ...prev,
                    name: event.target.value,
                  }
                : prev,
            );
          }}
          required
          autoFocus
        />
        <Select
          label="Plan Type"
          value={editingForm?.planCode ?? 'free'}
          onChange={(event) => {
            const value = event.target.value as PlanCode;
            setEditingForm((prev) =>
              prev
                ? {
                    ...prev,
                    planCode: value,
                  }
                : prev,
            );
          }}
        >
          {(Object.keys(PLAN_DISPLAY_NAME_BY_CODE) as PlanCode[]).map((code) => (
            <MenuItem key={code} value={code}>
              {PLAN_DISPLAY_NAME_BY_CODE[code]}
            </MenuItem>
          ))}
        </Select>
        <TextField
          label="Trial End"
          type="date"
          value={editingForm?.trialEndsAt ?? ''}
          onChange={(event) => {
            setEditingForm((prev) =>
              prev
                ? {
                    ...prev,
                    trialEndsAt: event.target.value,
                  }
                : prev,
            );
          }}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label="User Limit"
          type="number"
          value={editingForm?.userLimit ?? ''}
          onChange={(event) => {
            setEditingForm((prev) =>
              prev
                ? {
                    ...prev,
                    userLimit: event.target.value,
                  }
                : prev,
            );
          }}
          required
          slotProps={{ htmlInput: { min: 1, step: 1 } }}
        />
        <TextField
          label="Project Limit"
          type="number"
          value={editingForm?.projectLimit ?? ''}
          onChange={(event) => {
            setEditingForm((prev) =>
              prev
                ? {
                    ...prev,
                    projectLimit: event.target.value,
                  }
                : prev,
            );
          }}
          required
          slotProps={{ htmlInput: { min: 1, step: 1 } }}
        />
        <TextField
          label="Execution Upload Limit"
          type="number"
          value={editingForm?.executionUploadLimit ?? ''}
          onChange={(event) => {
            setEditingForm((prev) =>
              prev
                ? {
                    ...prev,
                    executionUploadLimit: event.target.value,
                  }
                : prev,
            );
          }}
          helperText="Leave blank for unlimited."
          slotProps={{ htmlInput: { min: 0, step: 1 } }}
        />
        <Select
          label="Delivery Entitlement"
          value={editingForm?.deliveryEnabled ? 'enabled' : 'disabled'}
          onChange={(event) => {
            const enabled = event.target.value === 'enabled';
            setEditingForm((prev) =>
              prev
                ? {
                    ...prev,
                    deliveryEnabled: enabled,
                  }
                : prev,
            );
          }}
        >
          <MenuItem value="enabled">Enabled</MenuItem>
          <MenuItem value="disabled">Disabled</MenuItem>
        </Select>
        <Select
          label="Operations Entitlement"
          value={editingForm?.operationsEnabled ? 'enabled' : 'disabled'}
          onChange={(event) => {
            const enabled = event.target.value === 'enabled';
            setEditingForm((prev) =>
              prev
                ? {
                    ...prev,
                    operationsEnabled: enabled,
                  }
                : prev,
            );
          }}
        >
          <MenuItem value="enabled">Enabled</MenuItem>
          <MenuItem value="disabled">Disabled</MenuItem>
        </Select>
        <Stack gap="sm" sx={{ pt: s('sm') }}>
          <Text role="label">Destructive Actions</Text>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap="sm" align={{ sm: 'center' }}>
            <Text role="body" color="textSecondary" sx={{ flex: 1 }}>
              Delete all project and execution data for this organisation.
            </Text>
            <Button
              variant="destructive"
              fullWidthOnMobile={false}
              disabled={!editingForm || pending}
              onClick={() => {
                setWipeNameInput('');
                setWipeOpen(true);
              }}
            >
              Wipe Data
            </Button>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap="sm" align={{ sm: 'center' }}>
            <Text role="body" color="textSecondary" sx={{ flex: 1 }}>
              Delete this organisation from the workspace.
            </Text>
            <Button
              variant="destructive"
              fullWidthOnMobile={false}
              disabled={!editingForm || pending}
              onClick={() => {
                setDeleteNameInput('');
                setDeleteOpen(true);
              }}
            >
              Delete Organisation
            </Button>
          </Stack>
        </Stack>
      </AddDialog>
      <ConfirmDialog
        open={wipeOpen}
        title="Wipe Organisation Data?"
        confirmLabel="Wipe Data"
        confirmColor="error"
        confirmDisabled={!wipeNameMatches}
        loading={pending}
        onClose={() => {
          if (pending) return;
          setWipeOpen(false);
          setWipeNameInput('');
        }}
        onConfirm={() => {
          if (!wipeNameMatches || pending) return;
          void wipeOrganisationData();
        }}
      >
        <Stack gap="md">
          <Text role="body" color="textSecondary">
            This action is permanent and cannot be undone.
          </Text>
          <Text role="body" color="textSecondary">
            Enter{' '}
            <Box component="code" sx={codeLabelSx}>
              {expectedOrganisationName}
            </Box>{' '}
            to confirm.
          </Text>
          <TextField
            label="Organisation Name"
            value={wipeNameInput}
            onChange={(event) => {
              setWipeNameInput(event.target.value);
            }}
            fullWidth
          />
        </Stack>
      </ConfirmDialog>
      <ConfirmDialog
        open={deleteOpen}
        title="Delete Organisation?"
        confirmLabel="Delete Organisation"
        confirmColor="error"
        confirmDisabled={!deleteNameMatches}
        loading={pending}
        onClose={() => {
          if (pending) return;
          setDeleteOpen(false);
          setDeleteNameInput('');
        }}
        onConfirm={() => {
          if (!deleteNameMatches || pending) return;
          void deleteOrganisation();
        }}
      >
        <Stack gap="md">
          <Text role="body" color="textSecondary">
            This action is permanent and cannot be undone.
          </Text>
          <Text role="body" color="textSecondary">
            Enter{' '}
            <Box component="code" sx={codeLabelSx}>
              {expectedOrganisationName}
            </Box>{' '}
            to confirm.
          </Text>
          <TextField
            label="Organisation Name"
            value={deleteNameInput}
            onChange={(event) => {
              setDeleteNameInput(event.target.value);
            }}
            fullWidth
          />
        </Stack>
      </ConfirmDialog>
    </>
  );
};
